import { useEffect, useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import { Paragraph, Spinner, YStack } from 'tamagui';
import { Screen } from '@mrrmlab/ui';
import { TokenEndpointError } from '@mrrmlab/auth';
import { useAppAuth } from '../app-context';

/**
 * OIDC redirect landing page. Parses the callback URL, exchanges the code
 * for tokens, and sends the user back to wherever they started the login
 * from (`returnTo`), or to the home page if nothing was remembered.
 */
function formatAuthError(err: unknown): string {
  if (err instanceof TokenEndpointError) {
    const body = err.body ? `\n${JSON.stringify(err.body, null, 2)}` : '';
    return `${err.message}${body}`;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

export function AuthCallbackRoute() {
  const auth = useAppAuth();
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!auth) {
      router.navigate({ to: '/', replace: true });
      return;
    }
    let cancelled = false;
    auth
      .handleCallback(window.location.href)
      .then(({ returnTo }) => {
        if (cancelled) return;
        const target = returnTo && returnTo.startsWith('/') ? returnTo : '/';
        router.navigate({ to: target, replace: true });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(formatAuthError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [auth, router]);

  return (
    <Screen>
      {error ? (
        <YStack gap="$2">
          <Paragraph color="$red10" style={{ whiteSpace: 'pre-wrap' }}>
            Login failed: {error}
          </Paragraph>
        </YStack>
      ) : (
        <YStack gap="$2" alignItems="flex-start">
          <Spinner />
          <Paragraph>Finishing sign-in…</Paragraph>
        </YStack>
      )}
    </Screen>
  );
}
